/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { useSearchParams } from "react-router-dom";
import { Divider, Radio } from "@mui/material";
import moment from "moment";
import {
  ButtonContainer,
  CarouselContainer,
  ConfirmationContainer,
  FormContainer,
  FormWrapper,
  LoaderContainer,
  StyledCaution,
  StyledConfirmButton,
  StyledNote,
  StyledQuestion,
  StyledRadioGroup,
  StyledRadioLabel,
  StyledSubTitle,
  StyledTitle,
} from "./styles";
import publicApi from "../../api/publicApi";
import { Loader } from "../../components/loader";
import { LoadingSpinner } from "../../components/loading_spinner";
import { CarouselPage } from "../../components/carousel";
import banner from "../../assets/images/banner.jpg";

// The token is read from a header rather than the query string: a query string is
// recorded on the request span and exported to the tracing backend, and this token
// archives a real person's records.
const CONFIRMATION_TOKEN_HEADER = "X-Confirmation-Token";

const GUEST_CONFIRMATION_PATH = "/v1/guest/inactivity_confirmation";

// The four states the GET reports are the whole contract — it always answers 200,
// so the HTTP status is never the branch.
const UNUSABLE_STATES = {
  invalid: {
    title: "Link not valid",
    message: "This confirmation link is not valid. It may have been mistyped, or replaced by a newer email.",
  },
  expired: {
    title: "Link expired",
    message: "This confirmation link has expired. Your restaurant can send you a new one.",
  },
  already_answered: {
    title: "Already answered",
    message: "This confirmation has already been answered, so there is nothing further to do.",
  },
};

const REQUEST_FAILED_MESSAGE = "We could not load this confirmation. Please check your connection and try again.";
const SUBMIT_FAILED_MESSAGE = "We could not record your answer. Please try again.";

const formatExpiry = (expiresAt) => {
  if (!expiresAt) return "";

  const parsed = moment.parseZone(expiresAt);
  return parsed.isValid() ? parsed.format("MMMM D, YYYY") : "";
};

const readErrorMessage = (error, fallbackMessage) => {
  const errors = error?.response?.data?.errors;

  if (Array.isArray(errors)) {
    const message = errors.find((entry) => typeof entry === "string" && entry.trim());
    if (message) return message;
  }

  return typeof errors === "string" && errors.trim() ? errors : fallbackMessage;
};

export const InactivityConfirmation = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [confirmation, setConfirmation] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [decision, setDecision] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [recorded, setRecorded] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const carouselData = [{ id: 1, image: banner }];

  React.useEffect(() => {
    // No react-query here on purpose: the response is scoped to one emailed token
    // and the server sets `no-store`, so it must not land in the client cache.
    let active = true;

    const loadConfirmation = async () => {
      if (!token) {
        setConfirmation({ state: "invalid" });
        setLoading(false);
        return;
      }

      try {
        const response = await publicApi.get(GUEST_CONFIRMATION_PATH, {
          headers: { [CONFIRMATION_TOKEN_HEADER]: token },
        });

        if (active) setConfirmation(response?.data || { state: "invalid" });
      } catch {
        if (active) setErrorMessage(REQUEST_FAILED_MESSAGE);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadConfirmation();

    return () => {
      active = false;
    };
  }, [token]);

  // Only ever from an explicit click. Email security scanners prefetch inbound
  // links, and the backend GET is inert by design — auto-posting would undo that.
  const handleConfirm = async () => {
    if (!decision || submitting) return;

    setSubmitting(true);
    setErrorMessage("");

    try {
      await publicApi.post(GUEST_CONFIRMATION_PATH, { token, decision });
      setRecorded(true);
    } catch (error) {
      const state = error?.response?.data?.state;

      // Another party may have settled the row since the page loaded, which comes
      // back as one of the unusable states rather than a retryable failure.
      if (UNUSABLE_STATES[state]) {
        setConfirmation({ ...confirmation, state });
      } else {
        setErrorMessage(readErrorMessage(error, SUBMIT_FAILED_MESSAGE));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      );
    }

    if (errorMessage && !confirmation) {
      return (
        <React.Fragment>
          <StyledTitle>Something went wrong</StyledTitle>
          <StyledSubTitle>{errorMessage}</StyledSubTitle>
        </React.Fragment>
      );
    }

    if (recorded) {
      return (
        <React.Fragment>
          <StyledTitle>Thank you</StyledTitle>
          <StyledSubTitle data-testid="confirmation-recorded">
            {decision === "active"
              ? `Your response has been recorded, and your access at ${confirmation?.restaurant_name} stays as it is.`
              : `Your response has been recorded. Your access at ${confirmation?.restaurant_name} will be given up.`}
          </StyledSubTitle>
        </React.Fragment>
      );
    }

    // Fail closed: only an explicit `valid` state reaches the answer form, so an
    // unrecognised or missing state reads as a dead link rather than a live one.
    if (confirmation?.state !== "valid") {
      const unusable = UNUSABLE_STATES[confirmation?.state] || UNUSABLE_STATES.invalid;

      return (
        <React.Fragment>
          <StyledTitle>{unusable.title}</StyledTitle>
          <StyledSubTitle data-testid={`confirmation-state-${confirmation?.state || "invalid"}`}>
            {unusable.message}
          </StyledSubTitle>
        </React.Fragment>
      );
    }

    const expiry = formatExpiry(confirmation?.expires_at);

    return (
      <React.Fragment>
        <StyledTitle>Needs confirmation</StyledTitle>
        <StyledSubTitle>
          {`Hi ${confirmation?.first_name || "there"}, based on your feedback the system will ensure that you `}
          {"currently actively work here or not."}
        </StyledSubTitle>
        <Divider
          style={{
            borderStyle: "solid",
            borderWidth: "2px",
            marginBottom: "20px",
            backgroundColor: "#252525",
          }}
        />

        <StyledQuestion>{`Do you currently work at ${confirmation?.restaurant_name}?`}</StyledQuestion>
        <StyledRadioGroup
          value={decision}
          onChange={(event) => setDecision(event.target.value)}
          data-testid="confirmation-decision-group"
        >
          <StyledRadioLabel value="active" control={<Radio size="small" />} label="Yes" />
          <StyledRadioLabel value="inactive" control={<Radio size="small" />} label="No" />
        </StyledRadioGroup>

        {decision === "inactive" && (
          <StyledCaution data-testid="confirmation-inactive-caution">
            {`Answering No gives up your discount access at ${confirmation?.restaurant_name}. `}
            {"This cannot be undone from this page."}
          </StyledCaution>
        )}

        {errorMessage && <StyledCaution data-testid="confirmation-error">{errorMessage}</StyledCaution>}

        <ButtonContainer>
          <StyledConfirmButton
            onClick={handleConfirm}
            disabled={!decision || submitting}
            data-testid="confirmation-submit"
          >
            {submitting ? <LoadingSpinner isLoading={true} /> : "Confirm"}
          </StyledConfirmButton>
        </ButtonContainer>

        {expiry && <StyledNote>{`This link can be used until ${expiry}.`}</StyledNote>}
      </React.Fragment>
    );
  };

  return (
    <ConfirmationContainer>
      <CarouselContainer>
        <CarouselPage data={carouselData} />
      </CarouselContainer>

      <FormContainer>
        <FormWrapper>{renderContent()}</FormWrapper>
      </FormContainer>
    </ConfirmationContainer>
  );
};
